import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import ReactMarkdown from 'react-markdown';
import TradingViewChart from '../finajan/TradingViewChart';
import finApi from '../finajan/api_client';
import { BarChart3, Building2, Newspaper, Target, ChevronDown, ChevronUp } from 'lucide-react';

const formatPrice = (price, type) => {
  if (!price) return '-';
  if (type === 'CRYPTO' || price < 1) return price.toFixed(price < 0.01 ? 6 : price < 1 ? 4 : 2);
  return price.toFixed(2);
};

const INDICATORS = ['RSI', 'MACD', 'EMA', 'Bollinger', 'Ichimoku', 'SuperTrend', 'Stochastic', 'ADX', 'OBV', 'Williams', 'VWAP'];
const SIG_COLORS = { AL: 'text-green-400 bg-green-900/40 border-green-700', SAT: 'text-red-400 bg-red-900/40 border-red-700', NÖTR: 'text-gray-400 bg-gray-800 border-gray-700', BEKLE: 'text-yellow-400 bg-yellow-900/40 border-yellow-700' };

// ============================================
// XAI Rapor Bölümlerini Ayıklama (Parse) Fonksiyonu
// ============================================
const parseXAISections = (report) => {
  if (!report) return null;
  
  const sections = {
    teknik: '',
    temel: '',
    haber: '',
    karar: '',
  };
  
  const markers = [
    { key: 'teknik', patterns: ['## TEKNIK GEREKCE', '## TEKNİK GEREKÇE', '## Teknik Gerekce', '## Teknik Gerekçe', '**TEKNIK GEREKCE**', '**TEKNİK GEREKÇE**'] },
    { key: 'temel', patterns: ['## TEMEL GEREKCE', '## TEMEL GEREKÇE', '## Temel Gerekce', '## Temel Gerekçe', '**TEMEL GEREKCE**', '**TEMEL GEREKÇE**'] },
    { key: 'haber', patterns: ['## HABER VE DUYARLILIK', '## HABER VE DUYARLILIK GEREKCESI', '## Haber ve Duyarlilik', '## HABER VE DUYARLILIK GEREKÇESİ', '**HABER VE DUYARLILIK**'] },
    { key: 'karar', patterns: ['## NIHAI YATIRIM', '## NİHAİ YATIRIM', '## Nihai Yatirim', '## NİHAİ YATIRIM DANIŞMANI', '**NIHAI YATIRIM**', '**NİHAİ YATIRIM**', 'NİHAİ KARAR', 'NIHAI KARAR'] },
  ];
  
  const lines = report.split('\n');
  let currentKey = null;
  
  for (const line of lines) {
    const upperLine = line.toUpperCase().replace(/[İĞÜŞÖÇ]/g, (c) => ({
      'İ': 'I', 'Ğ': 'G', 'Ü': 'U', 'Ş': 'S', 'Ö': 'O', 'Ç': 'C'
    }[c] || c));
    
    let matched = false;
    for (const marker of markers) {
      for (const pattern of marker.patterns) {
        if (upperLine.includes(pattern.toUpperCase().replace(/[İĞÜŞÖÇ]/g, (c) => ({
          'İ': 'I', 'Ğ': 'G', 'Ü': 'U', 'Ş': 'S', 'Ö': 'O', 'Ç': 'C'
        }[c] || c)))) {
          currentKey = marker.key;
          matched = true;
          break;
        }
      }
      if (matched) break;
    }
    
    if (!matched && currentKey) {
      sections[currentKey] += line + '\n';
    }
  }
  
  // Eğer hiçbir bölüm parse edilemezse tüm raporu 'karar' bölümüne koy
  const hasContent = Object.values(sections).some(s => s.trim().length > 0);
  if (!hasContent) {
    sections.karar = report;
  }
  
  return sections;
};

// ============================================
// XAI Açıklanabilir Yapay Zeka Rapor Kartları
// ============================================
const XAIReportView = ({ analysis }) => {
  const [expandedSections, setExpandedSections] = useState({
    teknik: true, temel: true, haber: true, karar: true
  });
  
  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };
  
  const sections = parseXAISections(analysis.llm_report);
  
  const sectionConfig = [
    {
      key: 'teknik',
      title: 'Teknik Gerekce',
      icon: BarChart3,
      gradient: 'from-blue-500/10 to-blue-600/5',
      borderColor: 'border-blue-500/30',
      iconColor: 'text-blue-400',
      headerBg: 'bg-blue-500/10',
    },
    {
      key: 'temel',
      title: 'Temel Gerekce',
      icon: Building2,
      gradient: 'from-emerald-500/10 to-emerald-600/5',
      borderColor: 'border-emerald-500/30',
      iconColor: 'text-emerald-400',
      headerBg: 'bg-emerald-500/10',
    },
    {
      key: 'haber',
      title: 'Haber ve Duyarlilik Gerekcesi',
      icon: Newspaper,
      gradient: 'from-amber-500/10 to-amber-600/5',
      borderColor: 'border-amber-500/30',
      iconColor: 'text-amber-400',
      headerBg: 'bg-amber-500/10',
    },
    {
      key: 'karar',
      title: 'Nihai Yatirim Danismani Karari',
      icon: Target,
      gradient: 'from-purple-500/10 to-purple-600/5',
      borderColor: 'border-purple-500/30',
      iconColor: 'text-purple-400',
      headerBg: 'bg-purple-500/10',
    },
  ];
  
  return (
    <div className="space-y-3" data-testid="xai-report-container">
      {/* Genel Sinyal Başlığı */}
      <Card className="bg-gray-900 border-gray-800">
        <CardContent className="py-3 px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-400">XAI Aciklanabilir Analiz Raporu</span>
            </div>
            <div className="flex items-center gap-3">
              {analysis.consensus_signal && (
                <span className={`px-4 py-1.5 rounded-lg border text-sm font-bold ${SIG_COLORS[analysis.consensus_signal] || SIG_COLORS.BEKLE}`} data-testid="xai-signal-badge">
                  {analysis.consensus_signal} ({analysis.confidence}%)
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Uyarılar */}
      {analysis.proximity_alerts?.length > 0 && (
        <div className="space-y-1">
          {analysis.proximity_alerts.map((a, i) => (
            <div key={i} className={`px-3 py-2 rounded text-sm ${a.includes('KRITIK') || a.includes('KRİTİK') || a.includes('ZORUNLU') ? 'bg-red-900/30 text-red-300 border border-red-700' : 'bg-yellow-900/20 text-yellow-300 border border-yellow-700'}`}>{a}</div>
          ))}
        </div>
      )}
      
      {/* Gösterge Etiketleri */}
      {analysis.selected_indicators && (
        <div className="flex flex-wrap gap-2">
          {analysis.selected_indicators.map((ind, i) => (
            <div key={i} className={`px-3 py-1.5 rounded-lg border text-xs ${SIG_COLORS[ind.signal] || SIG_COLORS['NÖTR']}`}>
              <span className="font-bold">{ind.indicator}</span>: {ind.signal} (WR: %{ind.win_rate})
            </div>
          ))}
        </div>
      )}
      
      {/* 4 XAI Bölümü Kartları */}
      {sections && sectionConfig.map(({ key, title, icon: Icon, gradient, borderColor, iconColor, headerBg }) => {
        const content = sections[key]?.trim();
        if (!content) return null;
        const isExpanded = expandedSections[key];
        
        return (
          <Card key={key} className={`bg-gradient-to-br ${gradient} border ${borderColor} overflow-hidden`} data-testid={`xai-section-${key}`}>
            <button 
              className={`w-full flex items-center justify-between px-4 py-3 ${headerBg} hover:opacity-80 transition-opacity`}
              onClick={() => toggleSection(key)}
              data-testid={`xai-toggle-${key}`}
            >
              <div className="flex items-center gap-2.5">
                <Icon className={`w-5 h-5 ${iconColor}`} />
                <span className="text-sm font-semibold text-white">{title}</span>
              </div>
              {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>
            {isExpanded && (
              <CardContent className="pt-3 pb-4">
                <div className="prose prose-sm prose-invert max-w-none">
                  <ReactMarkdown>{content}</ReactMarkdown>
                </div>
              </CardContent>
            )}
          </Card>
        );
      })}
      
      {/* Hata */}
      {analysis.error && <p className="text-red-400 text-sm">{analysis.error}</p>}
    </div>
  );
};

const FinAjanPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Erişim kontrolü
  if (!authLoading && user && !user.is_admin && !user.has_finajan_access) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center space-y-4">
          <BarChart3 className="w-16 h-16 text-gray-600 mx-auto" />
          <h2 className="text-xl font-bold text-white">Erişim Yetkisi Gerekli</h2>
          <p className="text-gray-400 text-sm max-w-md">FinAjan modülüne erişim yetkiniz bulunmamaktadır. Yöneticinizden erişim yetkisi talep edin.</p>
          <Button onClick={() => navigate('/dashboard')} className="bg-blue-600 hover:bg-blue-700">Ana Sayfaya Dön</Button>
        </div>
      </div>
    );
  }

  // State
  const [view, setView] = useState('dashboard'); // dashboard | analyze | portfolio
  const [marketType, setMarketType] = useState('BIST');
  const [performance, setPerformance] = useState(null);
  const [marketData, setMarketData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [ohlcvData, setOhlcvData] = useState([]);
  const [chartMarkers, setChartMarkers] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [search, setSearch] = useState('');
  const [portfolio, setPortfolio] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showAddPosition, setShowAddPosition] = useState(false);
  const [newPos, setNewPos] = useState({ symbol: '', name: '', entry_price: '', quantity: '1', tp_price: '', sl_price: '' });
  const [marketSentiment, setMarketSentiment] = useState(null);
  const [systemHealth, setSystemHealth] = useState(null);
  const [watchlist, setWatchlist] = useState([]);
  const [recommendation, setRecommendation] = useState(null);
  const [accuracyOverlay, setAccuracyOverlay] = useState(null);

  // Custom analyze form
  const [selectedIndicators, setSelectedIndicators] = useState([]);
  const [tp, setTp] = useState('');
  const [sl, setSl] = useState('');
  const [support, setSupport] = useState('');
  const [resistance, setResistance] = useState('');
  const [riskTolerance, setRiskTolerance] = useState('orta');
  const [timeHorizon, setTimeHorizon] = useState('orta');

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    if (!user.is_admin) { navigate('/'); return; }
    loadDashboard('BIST');
  }, [user, authLoading]);

  const loadDashboard = async (mtype) => {
    const mt = mtype || marketType;
    setLoading(true);
    try {
      const [perfRes, marketRes, sentRes, healthRes] = await Promise.all([
        finApi.getAgentPerformance().catch(() => ({ data: null })),
        finApi.getMarketOverview(mt).catch(() => ({ data: [] })),
        finApi.getMarketSentiment().catch(() => ({ data: null })),
        finApi.getSystemHealth().catch(() => ({ data: null }))
      ]);
      setPerformance(perfRes.data);
      setMarketData(marketRes.data || []);
      setMarketSentiment(sentRes.data);
      setSystemHealth(healthRes.data);
      // Watchlist
      finApi.getWatchlist().then(r => setWatchlist(r.data || [])).catch(() => {});
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadPortfolio = async () => {
    try {
      const [posRes, notifRes] = await Promise.all([
        finApi.getPortfolio('all').catch(() => ({ data: [] })),
        finApi.getNotifications().catch(() => ({ data: [] }))
      ]);
      setPortfolio(posRes.data || []);
      setNotifications(notifRes.data || []);
    } catch (e) { console.error(e); }
  };

  const handleAddPosition = async () => {
    if (!newPos.symbol || !newPos.entry_price) return;
    try {
      await finApi.addPosition({ symbol: newPos.symbol, name: newPos.name, entry_price: parseFloat(newPos.entry_price), quantity: parseFloat(newPos.quantity) || 1, tp_price: newPos.tp_price ? parseFloat(newPos.tp_price) : null, sl_price: newPos.sl_price ? parseFloat(newPos.sl_price) : null });
      setShowAddPosition(false);
      setNewPos({ symbol: '', name: '', entry_price: '', quantity: '1', tp_price: '', sl_price: '' });
      loadPortfolio();
    } catch (e) { alert('Hata: ' + e.message); }
  };

  const selectStock = async (stock) => {
    setSelectedSymbol(stock);
    setView('analyze');
    setAnalysis(null);
    setRecommendation(null);
    setAccuracyOverlay(null);
    try {
      const [ohlcvRes, histRes, recRes, accRes] = await Promise.all([
        finApi.getOHLCV(stock.symbol).catch(() => ({ data: [] })),
        finApi.getPredictionHistory(stock.symbol).catch(() => ({ data: [] })),
        finApi.getRecommendation(stock.symbol).catch(() => ({ data: null })),
        finApi.getAccuracyOverlay(stock.symbol).catch(() => ({ data: null }))
      ]);
      setOhlcvData(ohlcvRes.data || []);
      const markers = (histRes.data || []).filter(p => p.price_at_signal).map(p => ({
        time: Math.floor(new Date(p.created_at).getTime() / 1000),
        signal: p.signal,
      }));
      setChartMarkers(markers);
      if (recRes.data) setRecommendation(recRes.data);
      if (accRes.data) setAccuracyOverlay(accRes.data);
    } catch (e) { console.error(e); }
  };

  const runCustomAnalysis = async () => {
    if (!selectedSymbol) return;
    setAnalyzing(true);
    try {
      const params = {
        indicators: selectedIndicators.length > 0 ? selectedIndicators : null,
        take_profit: tp ? parseFloat(tp) : null,
        stop_loss: sl ? parseFloat(sl) : null,
        trading_range: (support || resistance) ? { support: support ? parseFloat(support) : null, resistance: resistance ? parseFloat(resistance) : null } : null,
        risk_tolerance: riskTolerance,
        time_horizon: timeHorizon,
      };
      const res = await finApi.customAnalyze(selectedSymbol.symbol, params);
      setAnalysis(res.data);
    } catch (e) {
      console.error(e);
      setAnalysis({ error: 'Analiz yapılamadı' });
    } finally { setAnalyzing(false); }
  };

  const hotSignals = marketData.filter(s => s.supertrend === 'AL' || s.supertrend === 'SAT');
  const filtered = marketData.filter(s => s.name?.toLowerCase().includes(search.toLowerCase()) || s.symbol?.toLowerCase().includes(search.toLowerCase()));

  if (authLoading) return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div></div>;
  if (!user?.is_admin) return null;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <header className="bg-[#0a0a0a]/95 border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-[1600px] mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-blue-600 rounded-lg flex items-center justify-center font-bold text-sm">FA</div>
            <h1 className="text-lg font-bold">FinAjan</h1>
            <div className="flex gap-1 ml-4">
              <Button size="sm" onClick={() => setView('dashboard')} className={`h-7 text-xs ${view === 'dashboard' ? 'bg-blue-600' : 'bg-gray-800 text-gray-300'}`}>Dashboard</Button>
              <Button size="sm" onClick={() => setView('analyze')} className={`h-7 text-xs ${view === 'analyze' ? 'bg-blue-600' : 'bg-gray-800 text-gray-300'}`}>Analiz</Button>
              <Button size="sm" onClick={() => { setView('portfolio'); loadPortfolio(); }} className={`h-7 text-xs ${view === 'portfolio' ? 'bg-blue-600' : 'bg-gray-800 text-gray-300'}`}>Portföyüm</Button>
              <Button size="sm" onClick={() => setView('report')} className={`h-7 text-xs ${view === 'report' ? 'bg-blue-600' : 'bg-gray-800 text-gray-300'}`}>Ajan Raporu</Button>
              <Button size="sm" onClick={() => setView('news')} className={`h-7 text-xs ${view === 'news' ? 'bg-blue-600' : 'bg-gray-800 text-gray-300'}`}>Haberler</Button>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => navigate('/site-admin')} variant="ghost" size="sm" className="text-gray-400 text-xs">Admin Panel</Button>
            <Button onClick={() => navigate('/')} variant="ghost" size="sm" className="text-gray-400 text-xs">Anasayfa</Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-4 py-4">

        {/* ===== DASHBOARD VIEW ===== */}
        {view === 'dashboard' && (
          <div className="space-y-4">
            {/* Piyasa Seçici */}
            <div className="flex items-center gap-2">
              {[{k:'BIST',l:'BIST',e:'🇹🇷'},{k:'CRYPTO',l:'Kripto',e:'₿'},{k:'US',l:'ABD',e:'🇺🇸'}].map(m => (
                <Button key={m.k} size="sm" onClick={() => { setMarketType(m.k); loadDashboard(m.k); }}
                  className={`h-8 px-4 text-sm ${marketType === m.k ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}>
                  {m.e} {m.l}
                </Button>
              ))}
            </div>
            {/* Performans Metrikleri */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card className="bg-gray-900 border-gray-800">
                <CardContent className="pt-4 pb-3">
                  <p className="text-gray-500 text-xs">3G Doğruluk</p>
                  <p className="text-2xl font-bold text-green-400">{performance?.evaluation_3d?.accuracy || 0}%</p>
                </CardContent>
              </Card>
              <Card className="bg-gray-900 border-gray-800">
                <CardContent className="pt-4 pb-3">
                  <p className="text-gray-500 text-xs">7G Doğruluk</p>
                  <p className="text-2xl font-bold text-blue-400">{performance?.evaluation_7d?.accuracy || 0}%</p>
                </CardContent>
              </Card>
              <Card className="bg-gray-900 border-gray-800">
                <CardContent className="pt-4 pb-3">
                  <p className="text-gray-500 text-xs">Toplam Tahmin</p>
                  <p className="text-2xl font-bold text-white">{performance?.total_predictions || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-gray-900 border-gray-800">
                <CardContent className="pt-4 pb-3">
                  <p className="text-gray-500 text-xs">Ort. PnL (7G)</p>
                  <p className={`text-2xl font-bold ${(performance?.evaluation_7d?.avg_pnl_pct || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {performance?.evaluation_7d?.avg_pnl_pct || 0}%
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Piyasa Nabzı Widget */}
            {marketSentiment && (
              <Card className="bg-gray-900 border-gray-800">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-white flex items-center justify-between">
                    Piyasa Nabzı
                    <span className={`text-lg font-bold ${marketSentiment.label === 'Boğa' ? 'text-green-400' : marketSentiment.label === 'Ayı' ? 'text-red-400' : 'text-gray-400'}`}>
                      {marketSentiment.label === 'Boğa' ? '📈 Boğa' : marketSentiment.label === 'Ayı' ? '📉 Ayı' : '➡️ Nötr'}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pb-3">
                  <div className="flex items-center gap-4 mb-3">
                    <div className="flex-1 bg-gray-800 rounded-full h-2 overflow-hidden">
                      <div className={`h-full ${marketSentiment.avg_score > 0 ? 'bg-green-500' : 'bg-red-500'}`} 
                        style={{ width: `${Math.min(Math.abs(marketSentiment.avg_score) * 100, 100)}%`, marginLeft: marketSentiment.avg_score < 0 ? 'auto' : 0 }}></div>
                    </div>
                    <span className="text-xs text-gray-400">{marketSentiment.avg_score?.toFixed(2)}</span>
                  </div>
                  <div className="flex gap-3 text-xs">
                    <span className="text-green-400">Pozitif: {marketSentiment.positive}</span>
                    <span className="text-gray-400">Nötr: {marketSentiment.neutral}</span>
                    <span className="text-red-400">Negatif: {marketSentiment.negative}</span>
                    <span className="text-gray-500 ml-auto">{marketSentiment.total_news} haber</span>
                  </div>
                  {marketSentiment.recent_news?.length > 0 && (
                    <div className="mt-3 space-y-1.5 max-h-32 overflow-y-auto">
                      {marketSentiment.recent_news.slice(0, 5).map((n, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs">
                          <span className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${n.sentiment_score > 0.1 ? 'bg-green-500' : n.sentiment_score < -0.1 ? 'bg-red-500' : 'bg-gray-600'}`}></span>
                          <span className="text-gray-300 truncate">{n.title}</span>
                          <span className="text-gray-600 flex-shrink-0">{n.source}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Sıcak Sinyaller */}
            {systemHealth && (
              <div className="flex gap-3 text-xs">
                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border ${systemHealth.cache?.bist?.fresh ? 'border-green-800 bg-green-900/20' : 'border-red-800 bg-red-900/20'}`}>
                  <span className={`w-2 h-2 rounded-full ${systemHealth.cache?.bist?.fresh ? 'bg-green-400' : 'bg-red-400'}`}></span>
                  <span className="text-gray-300">BIST: {systemHealth.cache?.bist?.count || 0}</span>
                </div>
                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border ${systemHealth.cache?.crypto?.fresh ? 'border-green-800 bg-green-900/20' : 'border-yellow-800 bg-yellow-900/20'}`}>
                  <span className={`w-2 h-2 rounded-full ${systemHealth.cache?.crypto?.fresh ? 'bg-green-400' : 'bg-yellow-400'}`}></span>
                  <span className="text-gray-300">Kripto: {systemHealth.cache?.crypto?.count || 0}</span>
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-green-800 bg-green-900/20">
                  <span className="w-2 h-2 rounded-full bg-green-400"></span>
                  <span className="text-gray-300">Ajanlar Aktif</span>
                </div>
              </div>
            )}
            {hotSignals.length > 0 && (
              <Card className="bg-gray-900 border-gray-800">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-amber-400">Sıcak Sinyaller (SuperTrend)</CardTitle></CardHeader>
                <CardContent className="pb-3">
                  <div className="flex flex-wrap gap-2">
                    {hotSignals.map(s => (
                      <button key={s.symbol} onClick={() => selectStock(s)}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm ${s.supertrend === 'AL' ? 'border-green-700 bg-green-900/20' : 'border-red-700 bg-red-900/20'}`}>
                        <span className={`font-bold ${s.supertrend === 'AL' ? 'text-green-400' : 'text-red-400'}`}>{s.supertrend}</span>
                        <span className="text-white">{s.name}</span>
                        <span className={`text-xs ${s.change_pct >= 0 ? 'text-green-400' : 'text-red-400'}`}>{s.change_pct >= 0 ? '+' : ''}{s.change_pct?.toFixed(1)}%</span>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Gözlem Listesi */}
            {watchlist.length > 0 && (
              <Card className="bg-gray-900 border-purple-800/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-purple-400">Gözlem Listesi ({watchlist.length})</CardTitle>
                </CardHeader>
                <CardContent className="pb-3">
                  <div className="flex flex-wrap gap-2">
                    {watchlist.map(w => {
                      const md = marketData.find(m => m.symbol === w.symbol);
                      return (
                        <div key={w.symbol} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-purple-800/50 bg-purple-900/10 text-sm">
                          <button onClick={() => { const s = md || {symbol: w.symbol, name: w.name}; selectStock(s); }} className="text-white hover:text-purple-300">{(w.symbol || '').replace('.IS','')}</button>
                          {md && <span className={`text-xs ${(md.change_pct || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>{md.change_pct >= 0 ? '+' : ''}{md.change_pct?.toFixed(1)}%</span>}
                          <button onClick={async () => { await finApi.removeFromWatchlist(w.symbol); setWatchlist(prev => prev.filter(x => x.symbol !== w.symbol)); }} className="text-gray-600 hover:text-red-400 text-xs">x</button>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Piyasa Tablosu */}
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm text-white">
                    {marketType === 'BIST' ? 'BIST Hisseleri' : marketType === 'CRYPTO' ? 'Kripto Paralar' : 'ABD Borsası'}
                  </CardTitle>
                  <input type="text" placeholder="Ara..." value={search} onChange={e => setSearch(e.target.value)}
                    className="px-3 py-1.5 text-xs bg-gray-800 border border-gray-700 rounded text-white w-48 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-4 space-y-3">
                    {[...Array(8)].map((_, i) => (
                      <div key={i} className="flex items-center gap-4 animate-pulse">
                        <div className="h-4 bg-gray-800 rounded w-20"></div>
                        <div className="h-4 bg-gray-800 rounded w-16"></div>
                        <div className="h-4 bg-gray-800 rounded w-12"></div>
                        <div className="h-4 bg-gray-800 rounded w-10"></div>
                        <div className="h-4 bg-gray-800 rounded w-10"></div>
                        <div className="h-4 bg-gray-800 rounded w-10"></div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="overflow-x-auto max-h-[55vh] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-800 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs text-gray-400">Hisse</th>
                          <th className="px-3 py-2 text-right text-xs text-gray-400">Fiyat</th>
                          <th className="px-3 py-2 text-right text-xs text-gray-400">Değişim</th>
                          <th className="px-3 py-2 text-center text-xs text-gray-400">RSI</th>
                          <th className="px-3 py-2 text-center text-xs text-gray-400">MACD</th>
                          <th className="px-3 py-2 text-center text-xs text-gray-400">SuperTrend</th>
                          <th className="px-3 py-2 text-center text-xs text-gray-400">EMA</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map(s => (
                          <tr key={s.symbol} onClick={() => selectStock(s)} className="cursor-pointer hover:bg-gray-800/50 border-b border-gray-800/50">
                            <td className="px-3 py-2.5">
                              <div className="font-medium text-white">{s.symbol.replace('.IS', '')}</div>
                              <div className="text-xs text-gray-500">{s.name}</div>
                            </td>
                            <td className="px-3 py-2.5 text-right font-medium">{s.price?.toFixed(2)}</td>
                            <td className={`px-3 py-2.5 text-right font-medium ${s.change_pct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {s.change_pct >= 0 ? '+' : ''}{s.change_pct?.toFixed(2)}%
                            </td>
                            <td className={`px-3 py-2.5 text-center text-xs ${s.rsi > 70 ? 'text-red-400' : s.rsi < 30 ? 'text-green-400' : 'text-gray-400'}`}>
                              {s.rsi?.toFixed(0) || '-'}
                            </td>
                            <td className="px-3 py-2.5 text-center"><span className={`text-xs px-1.5 py-0.5 rounded border ${SIG_COLORS[s.macd_signal] || SIG_COLORS.NÖTR}`}>{s.macd_signal || '-'}</span></td>
                            <td className="px-3 py-2.5 text-center"><span className={`text-xs px-1.5 py-0.5 rounded border font-bold ${SIG_COLORS[s.supertrend] || SIG_COLORS.NÖTR}`}>{s.supertrend || '-'}</span></td>
                            <td className="px-3 py-2.5 text-center"><span className={`text-xs px-1.5 py-0.5 rounded border ${SIG_COLORS[s.ema_signal] || SIG_COLORS.NÖTR}`}>{s.ema_signal || '-'}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* ===== PORTFOLIO VIEW ===== */}
        {view === 'portfolio' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">Portföyüm</h2>
              <Button onClick={() => setShowAddPosition(!showAddPosition)} className="bg-green-600 hover:bg-green-700 text-sm">
                + Pozisyon Ekle
              </Button>
            </div>

            {/* Pozisyon Ekleme Formu */}
            {showAddPosition && (
              <Card className="bg-gray-900 border-green-800/50">
                <CardContent className="pt-4 space-y-3">
                  <div className="grid grid-cols-6 gap-3">
                    <div>
                      <Label className="text-xs text-gray-400">Hisse</Label>
                      <select value={newPos.symbol} onChange={e => { const s = marketData.find(m => m.symbol === e.target.value); setNewPos({...newPos, symbol: e.target.value, name: s?.name || ''}); }}
                        className="w-full h-8 bg-gray-800 border border-gray-700 rounded text-white text-xs px-2">
                        <option value="">Seç</option>
                        {marketData.map(s => <option key={s.symbol} value={s.symbol}>{s.symbol.replace('.IS','')} - {s.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-400">Alış Fiyatı</Label>
                      <Input type="number" value={newPos.entry_price} onChange={e => setNewPos({...newPos, entry_price: e.target.value})} className="bg-gray-800 border-gray-700 text-white text-xs h-8" placeholder="₺" />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-400">Adet</Label>
                      <Input type="number" value={newPos.quantity} onChange={e => setNewPos({...newPos, quantity: e.target.value})} className="bg-gray-800 border-gray-700 text-white text-xs h-8" />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-400">TP</Label>
                      <Input type="number" value={newPos.tp_price} onChange={e => setNewPos({...newPos, tp_price: e.target.value})} className="bg-gray-800 border-gray-700 text-white text-xs h-8" placeholder="₺" />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-400">SL</Label>
                      <Input type="number" value={newPos.sl_price} onChange={e => setNewPos({...newPos, sl_price: e.target.value})} className="bg-gray-800 border-gray-700 text-white text-xs h-8" placeholder="₺" />
                    </div>
                    <div className="flex items-end">
                      <Button onClick={handleAddPosition} className="bg-green-600 hover:bg-green-700 h-8 text-xs w-full">Ekle</Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Özet Kartları */}
            {(() => {
              const open = portfolio.filter(p => p.status === 'open');
              const totalPnl = open.reduce((s, p) => s + (p.live_pnl_tl || 0), 0);
              const totalVal = open.reduce((s, p) => s + (p.current_price || p.entry_price) * (p.quantity || 1), 0);
              return (
                <div className="grid grid-cols-4 gap-3">
                  <Card className="bg-gray-900 border-gray-800"><CardContent className="pt-3 pb-3"><p className="text-gray-500 text-xs">Açık Pozisyon</p><p className="text-xl font-bold text-white">{open.length}</p></CardContent></Card>
                  <Card className="bg-gray-900 border-gray-800"><CardContent className="pt-3 pb-3"><p className="text-gray-500 text-xs">Portföy Değeri</p><p className="text-xl font-bold text-white">{totalVal.toLocaleString('tr-TR', {minimumFractionDigits: 0})} ₺</p></CardContent></Card>
                  <Card className="bg-gray-900 border-gray-800"><CardContent className="pt-3 pb-3"><p className="text-gray-500 text-xs">Toplam K/Z</p><p className={`text-xl font-bold ${totalPnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>{totalPnl >= 0 ? '+' : ''}{totalPnl.toLocaleString('tr-TR', {minimumFractionDigits: 2})} ₺</p></CardContent></Card>
                  <Card className="bg-gray-900 border-gray-800"><CardContent className="pt-3 pb-3"><p className="text-gray-500 text-xs">Bildirim</p><p className="text-xl font-bold text-amber-400">{notifications.length}</p></CardContent></Card>
                </div>
              );
            })()}

            {/* Pozisyon Tablosu */}
            <Card className="bg-gray-900 border-gray-800">
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead className="bg-gray-800">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs text-gray-400">Hisse</th>
                      <th className="px-3 py-2 text-right text-xs text-gray-400">Alış</th>
                      <th className="px-3 py-2 text-right text-xs text-gray-400">Güncel</th>
                      <th className="px-3 py-2 text-right text-xs text-gray-400">Adet</th>
                      <th className="px-3 py-2 text-right text-xs text-gray-400">K/Z %</th>
                      <th className="px-3 py-2 text-right text-xs text-gray-400">K/Z ₺</th>
                      <th className="px-3 py-2 text-center text-xs text-gray-400">TP/SL</th>
                      <th className="px-3 py-2 text-center text-xs text-gray-400">Durum</th>
                      <th className="px-3 py-2 text-right text-xs text-gray-400">İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {portfolio.map(p => (
                      <tr key={p.id} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                        <td className="px-3 py-2.5">
                          <div className="font-medium text-white">{(p.symbol || '').replace('.IS','')}</div>
                          <div className="text-xs text-gray-500">{p.name}</div>
                        </td>
                        <td className="px-3 py-2.5 text-right">{p.entry_price?.toFixed(2)}</td>
                        <td className="px-3 py-2.5 text-right font-medium">{p.current_price?.toFixed(2) || '-'}</td>
                        <td className="px-3 py-2.5 text-right">{p.quantity}</td>
                        <td className={`px-3 py-2.5 text-right font-bold ${(p.live_pnl_pct || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {p.live_pnl_pct != null ? `${p.live_pnl_pct >= 0 ? '+' : ''}${p.live_pnl_pct}%` : (p.pnl_pct != null ? `${p.pnl_pct}%` : '-')}
                        </td>
                        <td className={`px-3 py-2.5 text-right ${(p.live_pnl_tl || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {p.live_pnl_tl != null ? `${p.live_pnl_tl >= 0 ? '+' : ''}${p.live_pnl_tl.toFixed(0)}₺` : '-'}
                        </td>
                        <td className="px-3 py-2.5 text-center text-xs">
                          {p.tp_price && <span className="text-green-400">TP:{p.tp_price}</span>}
                          {p.tp_price && p.sl_price && ' / '}
                          {p.sl_price && <span className="text-red-400">SL:{p.sl_price}</span>}
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <span className={`text-xs px-2 py-0.5 rounded ${p.status === 'open' ? 'bg-green-900/40 text-green-300' : 'bg-gray-800 text-gray-400'}`}>
                            {p.status === 'open' ? 'Açık' : 'Kapalı'}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          {p.status === 'open' && (
                            <div className="flex gap-1 justify-end">
                              <button onClick={() => { selectStock({symbol: p.symbol, name: p.name}); setView('analyze'); }} className="text-xs text-blue-400 hover:text-blue-300">Analiz</button>
                              <button onClick={async () => {
                                const cp = p.current_price || prompt('Çıkış fiyatı:');
                                if (cp) { await finApi.closePosition(p.id, parseFloat(cp)); loadPortfolio(); }
                              }} className="text-xs text-amber-400 hover:text-amber-300">Kapat</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {portfolio.length === 0 && <p className="text-center py-8 text-gray-600">Henüz pozisyon yok</p>}
              </CardContent>
            </Card>

            {/* Bildirimler */}
            {notifications.length > 0 && (
              <Card className="bg-gray-900 border-gray-800">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-amber-400">Son Bildirimler</CardTitle></CardHeader>
                <CardContent className="p-0 max-h-48 overflow-y-auto">
                  {notifications.slice(0, 10).map((n, i) => (
                    <div key={i} className={`px-4 py-2 border-b border-gray-800/50 ${n.severity === 'critical' ? 'bg-red-900/10' : n.severity === 'warning' ? 'bg-yellow-900/10' : ''}`}>
                      <div className="flex justify-between">
                        <span className="text-sm text-white">{n.title}</span>
                        <span className="text-xs text-gray-500">{n.created_at ? new Date(n.created_at).toLocaleString('tr-TR') : ''}</span>
                      </div>
                      <p className="text-xs text-gray-400">{n.message}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* ===== AJAN RAPORU VIEW ===== */}
        {view === 'report' && <AgentReportView />}

        {/* ===== HABERLER VIEW ===== */}
        {view === 'news' && <NewsView />}

        {/* ===== ANALYZE VIEW ===== */}
        {view === 'analyze' && (
          <div className="grid lg:grid-cols-4 gap-4">
            {/* Sol: Grafik (3/4) */}
            <div className="lg:col-span-3 space-y-4">
              {selectedSymbol ? (
                <>
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-bold">{selectedSymbol.name} <span className="text-gray-500 text-sm">{selectedSymbol.symbol}</span></h2>
                      {selectedSymbol.price && <p className="text-sm text-gray-400">Fiyat: {selectedSymbol.price?.toFixed(2)} TL | {selectedSymbol.change_pct >= 0 ? '+' : ''}{selectedSymbol.change_pct?.toFixed(2)}%</p>}
                    </div>
                    <Button onClick={() => { setView('dashboard'); setSelectedSymbol(null); }} variant="ghost" size="sm" className="text-gray-400">Dashboard'a Dön</Button>
                    <Button onClick={async () => {
                      try {
                        let res;
                        if (analysis) {
                          // Mevcut analiz sonucu varsa hizli PDF
                          res = await finApi.downloadReportFromAnalysis(analysis);
                        } else {
                          res = await finApi.downloadReport(selectedSymbol.symbol);
                        }
                        const url = window.URL.createObjectURL(new Blob([res.data]));
                        const a = document.createElement('a'); a.href = url;
                        a.setAttribute('download', `FinAjan_${selectedSymbol.symbol.replace('.IS','')}.pdf`);
                        document.body.appendChild(a); a.click(); a.remove();
                        window.URL.revokeObjectURL(url);
                      } catch(e) { console.error(e); alert('PDF oluşturulamadı: ' + (e.response?.data?.detail || e.message)); }
                    }} size="sm" className="bg-blue-600 hover:bg-blue-700 text-xs" data-testid="pdf-download-btn">PDF Rapor</Button>
                    <Button onClick={async () => {
                      try {
                        await finApi.addToWatchlist({symbol: selectedSymbol.symbol, name: selectedSymbol.name});
                        alert('Gözlem listesine eklendi!');
                      } catch(e) { alert('Hata'); }
                    }} size="sm" className="bg-purple-600 hover:bg-purple-700 text-xs">Takibe Al</Button>
                  </div>

                  {/* TradingView Grafik */}
                  <Card className="bg-gray-900 border-gray-800 overflow-hidden">
                    <CardContent className="p-0">
                      {ohlcvData.length > 0 ? (
                        <TradingViewChart ohlcvData={ohlcvData} markers={chartMarkers} height={450} />
                      ) : (
                        <div className="h-[450px] bg-gray-900 animate-pulse flex flex-col items-center justify-center gap-3">
                          <div className="w-12 h-12 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                          <p className="text-gray-600 text-sm">Grafik verisi yükleniyor...</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Accuracy Overlay Bar Chart */}
                  {accuracyOverlay && accuracyOverlay.bars && (
                    <Card className="bg-gray-900 border-gray-800" data-testid="accuracy-overlay-card">
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-sm text-white flex items-center gap-2">
                            Ajan Dogruluk Overlay
                            <span className="text-xs text-gray-500">(Vade Bazli)</span>
                          </CardTitle>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400">Genel: </span>
                            <span className={`text-sm font-bold ${(accuracyOverlay.overall_accuracy || 0) >= 70 ? 'text-green-400' : (accuracyOverlay.overall_accuracy || 0) >= 50 ? 'text-yellow-400' : 'text-red-400'}`} data-testid="accuracy-overall">
                              %{accuracyOverlay.overall_accuracy || 0}
                            </span>
                            <span className="text-xs text-gray-600">({accuracyOverlay.total_predictions || 0} tahmin)</span>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="pb-3">
                        <div className="flex items-end gap-4 h-32" data-testid="accuracy-bars">
                          {accuracyOverlay.bars.map((bar, idx) => {
                            const maxHeight = 100;
                            const barH = bar.accuracy > 0 ? Math.max((bar.accuracy / 100) * maxHeight, 8) : 4;
                            const barColor = bar.accuracy >= 70 ? 'bg-green-500' : bar.accuracy >= 50 ? 'bg-yellow-500' : bar.accuracy > 0 ? 'bg-red-500' : 'bg-gray-700';
                            return (
                              <div key={idx} className="flex-1 flex flex-col items-center gap-1" data-testid={`accuracy-bar-${bar.timeframe}`}>
                                <span className={`text-xs font-bold ${bar.accuracy >= 70 ? 'text-green-400' : bar.accuracy >= 50 ? 'text-yellow-400' : bar.accuracy > 0 ? 'text-red-400' : 'text-gray-600'}`}>
                                  {bar.accuracy > 0 ? `%${bar.accuracy}` : '-'}
                                </span>
                                <div className="w-full flex justify-center" style={{ height: `${maxHeight}px` }}>
                                  <div className={`w-10 rounded-t-md ${barColor} transition-all duration-500`} 
                                    style={{ height: `${barH}px`, marginTop: `${maxHeight - barH}px` }}
                                    title={`${bar.label}: %${bar.accuracy} (${bar.success}/${bar.evaluated})`}>
                                  </div>
                                </div>
                                <span className="text-xs text-gray-400 font-medium">{bar.label}</span>
                                <span className="text-[10px] text-gray-600">{bar.success}/{bar.evaluated}</span>
                              </div>
                            );
                          })}
                        </div>
                        <div className="flex justify-center gap-6 mt-3 pt-2 border-t border-gray-800">
                          <div className="flex items-center gap-1.5">
                            <div className="w-2.5 h-2.5 rounded-sm bg-green-500"></div>
                            <span className="text-[10px] text-gray-500">70%+ Basarili</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <div className="w-2.5 h-2.5 rounded-sm bg-yellow-500"></div>
                            <span className="text-[10px] text-gray-500">50-69% Orta</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <div className="w-2.5 h-2.5 rounded-sm bg-red-500"></div>
                            <span className="text-[10px] text-gray-500">50%- Dusuk</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* XAI Aciklanabilir AI Rapor */}
                  {analysis && (
                    <XAIReportView analysis={analysis} />
                  )}
                </>
              ) : (
                <div className="flex items-center justify-center h-[500px] text-gray-600">
                  <div className="text-center">
                    <p className="text-lg mb-2">Hisse seçin</p>
                    <p className="text-sm">Dashboard'dan bir hisseye tıklayın veya aşağıdan seçin</p>
                  </div>
                </div>
              )}
            </div>

            {/* Sağ: Kontrol Paneli (1/4) */}
            <div className="space-y-4">
              {/* Hisse Seçimi */}
              <Card className="bg-gray-900 border-gray-800">
                <CardHeader className="pb-2"><CardTitle className="text-xs text-gray-400">Hisse Seç</CardTitle></CardHeader>
                <CardContent className="p-2 max-h-40 overflow-y-auto">
                  {marketData.slice(0, 15).map(s => (
                    <button key={s.symbol} onClick={() => selectStock(s)}
                      className={`w-full text-left px-2 py-1.5 rounded text-xs hover:bg-gray-800 ${selectedSymbol?.symbol === s.symbol ? 'bg-blue-900/30 text-blue-400' : 'text-gray-300'}`}>
                      {s.symbol.replace('.IS', '')} - {s.name}
                    </button>
                  ))}
                </CardContent>
              </Card>

              {/* Ajanı Yönlendir */}
              <Card className="bg-gray-900 border-gray-800">
                <CardHeader className="pb-2"><CardTitle className="text-xs text-amber-400">Ajanı Yönlendir</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {/* Ajanın Notu */}
                  {recommendation && (
                    <div className="p-2.5 bg-blue-900/20 border border-blue-800/50 rounded-lg">
                      <p className="text-xs text-blue-300 mb-1 font-medium">Ajanın Notu:</p>
                      <p className="text-xs text-gray-300 leading-relaxed">{recommendation.message}</p>
                      {recommendation.recommended?.length > 0 && (
                        <Button size="sm" onClick={() => setSelectedIndicators(recommendation.recommended)}
                          className="mt-2 h-6 text-xs bg-blue-700 hover:bg-blue-600 w-full">Önerilenleri Seç</Button>
                      )}
                    </div>
                  )}
                  {/* Gösterge Seçimi */}
                  <div>
                    <Label className="text-xs text-gray-400">Göstergeler</Label>
                    <div className="grid grid-cols-2 gap-1 mt-1">
                      {INDICATORS.map(ind => (
                        <label key={ind} className="flex items-center gap-1.5 px-2 py-1 rounded bg-gray-800/50 hover:bg-gray-800 cursor-pointer">
                          <Checkbox checked={selectedIndicators.includes(ind)}
                            onCheckedChange={(checked) => {
                              setSelectedIndicators(prev => checked ? [...prev, ind] : prev.filter(i => i !== ind));
                            }} className="w-3.5 h-3.5 border-gray-600" />
                          <span className="text-xs text-gray-300">{ind}</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-gray-600 mt-1">{selectedIndicators.length === 0 ? 'Boş = Sistem seçer' : `${selectedIndicators.length} seçili`}</p>
                  </div>

                  {/* TP / SL */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-gray-400">Hedef (TP)</Label>
                      <Input type="number" value={tp} onChange={e => setTp(e.target.value)} placeholder="₺" className="bg-gray-800 border-gray-700 text-white text-xs h-8" />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-400">Stop Loss</Label>
                      <Input type="number" value={sl} onChange={e => setSl(e.target.value)} placeholder="₺" className="bg-gray-800 border-gray-700 text-white text-xs h-8" />
                    </div>
                  </div>

                  {/* Destek / Direnç */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-gray-400">Destek</Label>
                      <Input type="number" value={support} onChange={e => setSupport(e.target.value)} placeholder="₺" className="bg-gray-800 border-gray-700 text-white text-xs h-8" />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-400">Direnç</Label>
                      <Input type="number" value={resistance} onChange={e => setResistance(e.target.value)} placeholder="₺" className="bg-gray-800 border-gray-700 text-white text-xs h-8" />
                    </div>
                  </div>

                  {/* Risk / Zaman */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-gray-400">Risk</Label>
                      <div className="flex gap-1">
                        {['düşük', 'orta', 'yüksek'].map(r => (
                          <button key={r} onClick={() => setRiskTolerance(r)}
                            className={`flex-1 text-xs py-1 rounded ${riskTolerance === r ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400'}`}>{r}</button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-400">Vade</Label>
                      <div className="flex gap-1">
                        {['kısa', 'orta', 'uzun'].map(t => (
                          <button key={t} onClick={() => setTimeHorizon(t)}
                            className={`flex-1 text-xs py-1 rounded ${timeHorizon === t ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400'}`}>{t}</button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <Button onClick={runCustomAnalysis} disabled={!selectedSymbol || analyzing} className="w-full bg-amber-600 hover:bg-amber-700 text-white text-sm">
                    {analyzing ? 'Ajan analiz ediyor...' : 'Analizi Başlat'}
                  </Button>
                  {analyzing && (
                    <div className="mt-2 p-3 bg-blue-900/20 border border-blue-800/50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-xs text-blue-300">Ajan çalışıyor</span>
                      </div>
                      <div className="space-y-1 text-xs text-gray-500">
                        <p>1. Piyasa verileri analiz ediliyor...</p>
                        <p>2. Backtest sonuçları değerlendiriliyor...</p>
                        <p>3. Geçmiş tahminlerden ders çıkarılıyor...</p>
                        <p>4. AI raporu oluşturuluyor...</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const NewsView = () => {
  const [news, setNews] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    finApi.getAllNews('ALL', 200).then(r => setNews(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">Tüm Haberler ({news.length})</h2>
      <Card className="bg-gray-900 border-gray-800">
        <CardContent className="p-0 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div></div>
          ) : (
            <div className="divide-y divide-gray-800">
              {news.map((n, i) => (
                <div key={i} className="px-4 py-3 hover:bg-gray-800/30">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${(n.sentiment_score||0) > 0.1 ? 'bg-green-500' : (n.sentiment_score||0) < -0.1 ? 'bg-red-500' : 'bg-gray-600'}`}></span>
                        <span className="text-xs text-gray-500">{n.source}</span>
                        {n.symbol && n.symbol !== 'BIST_GENEL' && <span className="text-xs bg-blue-900/50 text-blue-300 px-1.5 py-0.5 rounded">{(n.symbol||'').replace('.IS','')}</span>}
                        <span className={`text-xs px-1.5 py-0.5 rounded ${(n.sentiment_score||0) > 0.1 ? 'bg-green-900/30 text-green-400' : (n.sentiment_score||0) < -0.1 ? 'bg-red-900/30 text-red-400' : 'text-gray-500'}`}>
                          {n.sentiment_score?.toFixed(2) || '0'}
                        </span>
                      </div>
                      <a href={n.url} target="_blank" rel="noopener noreferrer" className="text-sm text-white hover:text-blue-400">{n.title}</a>
                      {n.summary && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{n.summary?.replace(/<[^>]*>/g, '').substring(0, 150)}</p>}
                    </div>
                    <span className="text-xs text-gray-600 whitespace-nowrap">{(n.analyzed_at||'').substring(0, 16)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const AgentReportView = () => {
  const [period, setPeriod] = React.useState('7d');
  const [report, setReport] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [scorecard, setScorecard] = React.useState([]);

  React.useEffect(() => { loadReport(); }, [period]);

  const loadReport = async () => {
    setLoading(true);
    try {
      const [repRes, scRes] = await Promise.all([
        finApi.getAgentReport(period),
        finApi.getWatchlistScorecard().catch(() => ({ data: [] }))
      ]);
      setReport(repRes.data);
      setScorecard(scRes.data || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const R3 = report?.summary || {};
  const R7 = report?.summary_7d || {};

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Ajan Performans Raporu</h2>
        <div className="flex gap-1">
          {[{k:'1d',l:'Bugün'},{k:'7d',l:'Hafta'},{k:'30d',l:'Ay'},{k:'90d',l:'3 Ay'}].map(p => (
            <Button key={p.k} size="sm" onClick={() => setPeriod(p.k)}
              className={`h-7 text-xs ${period === p.k ? 'bg-blue-600' : 'bg-gray-800 text-gray-300'}`}>{p.l}</Button>
          ))}
          <Button size="sm" onClick={async () => {
            try {
              const res = await finApi.getAgentReportPDF(period);
              const url = window.URL.createObjectURL(new Blob([res.data]));
              const a = document.createElement('a'); a.href = url;
              a.setAttribute('download', `FinAjan_Rapor_${period}.pdf`);
              document.body.appendChild(a); a.click(); a.remove();
            } catch(e) { alert('PDF oluşturulamadı'); }
          }} className="h-7 text-xs bg-blue-600 hover:bg-blue-700">PDF İndir</Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div></div>
      ) : report ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card className="bg-gray-900 border-gray-800"><CardContent className="pt-3 pb-3"><p className="text-gray-500 text-xs">Toplam Tahmin</p><p className="text-2xl font-bold text-white">{report.total_predictions}</p></CardContent></Card>
            <Card className="bg-gray-900 border-gray-800"><CardContent className="pt-3 pb-3"><p className="text-gray-500 text-xs">3G Doğruluk</p><p className="text-2xl font-bold text-green-400">%{R3.accuracy || 0}</p></CardContent></Card>
            <Card className="bg-gray-900 border-gray-800"><CardContent className="pt-3 pb-3"><p className="text-gray-500 text-xs">7G Doğruluk</p><p className="text-2xl font-bold text-blue-400">%{R7.accuracy || 0}</p></CardContent></Card>
            <Card className="bg-gray-900 border-gray-800"><CardContent className="pt-3 pb-3"><p className="text-gray-500 text-xs">Ort. PnL (3G)</p><p className={`text-2xl font-bold ${(R3.avg_pnl_pct||0)>=0?'text-green-400':'text-red-400'}`}>%{R3.avg_pnl_pct||0}</p></CardContent></Card>
            <Card className="bg-gray-900 border-gray-800"><CardContent className="pt-3 pb-3"><p className="text-gray-500 text-xs">Ort. PnL (7G)</p><p className={`text-2xl font-bold ${(R7.avg_pnl_pct||0)>=0?'text-green-400':'text-red-400'}`}>%{R7.avg_pnl_pct||0}</p></CardContent></Card>
          </div>

          <Card className="bg-gray-900 border-gray-800">
            <CardHeader className="pb-2"><CardTitle className="text-sm text-white">Tahmin vs Gerçek Karşılaştırma</CardTitle></CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs text-gray-400">Tarih</th>
                    <th className="px-3 py-2 text-left text-xs text-gray-400">Hisse</th>
                    <th className="px-3 py-2 text-center text-xs text-gray-400">Sinyal</th>
                    <th className="px-3 py-2 text-right text-xs text-gray-400">Fiyat</th>
                    <th className="px-3 py-2 text-right text-xs text-gray-400">3G Sonrası</th>
                    <th className="px-3 py-2 text-center text-xs text-gray-400">3G Sonuç</th>
                    <th className="px-3 py-2 text-right text-xs text-gray-400">7G Sonrası</th>
                    <th className="px-3 py-2 text-center text-xs text-gray-400">7G Sonuç</th>
                  </tr>
                </thead>
                <tbody>
                  {(report.comparison || []).map((c, i) => (
                    <tr key={i} className="border-b border-gray-800/50">
                      <td className="px-3 py-2 text-gray-400 text-xs">{c.date}</td>
                      <td className="px-3 py-2 text-white">{(c.symbol||'').replace('.IS','')}</td>
                      <td className="px-3 py-2 text-center"><span className={`text-xs px-2 py-0.5 rounded border font-bold ${SIG_COLORS[c.signal]||SIG_COLORS.BEKLE}`}>{c.signal}</span></td>
                      <td className="px-3 py-2 text-right">{c.price_at_signal?.toFixed(2)}</td>
                      <td className={`px-3 py-2 text-right ${(c.pnl_3d||0)>=0?'text-green-400':'text-red-400'}`}>{c.price_3d?.toFixed(2)||'-'} {c.pnl_3d!=null?`(${c.pnl_3d>0?'+':''}${c.pnl_3d}%)`:''}</td>
                      <td className="px-3 py-2 text-center"><span className={`text-xs ${c.result_3d==='basarili'?'text-green-400':c.result_3d==='basarisiz'?'text-red-400':'text-gray-500'}`}>{c.result_3d==='basarili'?'✓':c.result_3d==='basarisiz'?'✗':'⏳'}</span></td>
                      <td className={`px-3 py-2 text-right ${(c.pnl_7d||0)>=0?'text-green-400':'text-red-400'}`}>{c.price_7d?.toFixed(2)||'-'} {c.pnl_7d!=null?`(${c.pnl_7d>0?'+':''}${c.pnl_7d}%)`:''}</td>
                      <td className="px-3 py-2 text-center"><span className={`text-xs ${c.result_7d==='basarili'?'text-green-400':c.result_7d==='basarisiz'?'text-red-400':'text-gray-500'}`}>{c.result_7d==='basarili'?'✓':c.result_7d==='basarisiz'?'✗':'⏳'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(!report.comparison || report.comparison.length === 0) && <p className="text-center py-8 text-gray-600">Henüz tahmin verisi yok. Analiz yapıldıkça burada görünecek.</p>}
            </CardContent>
          </Card>

          {/* Gözlem Karnesi */}
          {scorecard.length > 0 && (
            <Card className="bg-gray-900 border-purple-800/50">
              <CardHeader className="pb-2"><CardTitle className="text-sm text-purple-400">Gözlem Karnesi (Shadow Mode)</CardTitle></CardHeader>
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead className="bg-gray-800">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs text-gray-400">Hisse</th>
                      <th className="px-3 py-2 text-center text-xs text-gray-400">Tahmin</th>
                      <th className="px-3 py-2 text-center text-xs text-gray-400">Değerlendirilen</th>
                      <th className="px-3 py-2 text-center text-xs text-gray-400">Başarılı</th>
                      <th className="px-3 py-2 text-center text-xs text-gray-400">Doğruluk</th>
                      <th className="px-3 py-2 text-center text-xs text-gray-400">Durum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scorecard.map((sc, i) => (
                      <tr key={i} className="border-b border-gray-800/50">
                        <td className="px-3 py-2"><span className="text-white font-medium">{(sc.symbol||'').replace('.IS','')}</span> <span className="text-gray-500 text-xs">{sc.name}</span></td>
                        <td className="px-3 py-2 text-center text-white">{sc.total_predictions || 0}</td>
                        <td className="px-3 py-2 text-center text-gray-400">{sc.evaluated_7d || 0}</td>
                        <td className="px-3 py-2 text-center text-green-400">{sc.successful_7d || 0}</td>
                        <td className="px-3 py-2 text-center"><span className={`font-bold ${(sc.accuracy_7d||0) >= 70 ? 'text-green-400' : (sc.accuracy_7d||0) >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>%{sc.accuracy_7d || 0}</span></td>
                        <td className="px-3 py-2 text-center">{sc.ready_for_real ? <span className="text-xs bg-green-900/50 text-green-300 px-2 py-0.5 rounded">Portföye Hazır</span> : <span className="text-xs bg-yellow-900/50 text-yellow-300 px-2 py-0.5 rounded">Gözlemde</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="px-4 py-2 text-xs text-gray-600">%70+ doğruluk ve 5+ değerlendirme = Portföye Hazır</p>
              </CardContent>
            </Card>
          )}
        </>
      ) : null}
    </div>
  );
};

export default FinAjanPage;
